/**
 * iter63z'''''''''' — "Your Tiny Crew" roster modal.
 *
 * Single-tournament leagues' replacement for the Crown Points link.
 * Lists every player (a.k.a. "Your Crewmates") with their current rank,
 * points, and admin/co-admin badge. Wraps the existing board prop so we
 * don't fetch anything new — just present what the dashboard already
 * has.
 *
 * iter84.23 — Crew identity polish: each row shows the crewmate's
 * `avatar_emoji` (fallback to a 🙂) + their one-line `crew_flair`. The
 * current user can expand an inline editor to set both.
 */
import { useState, useEffect } from "react";
import { Crown, Shield, Users, X, Sparkles, ChevronDown, ChevronUp, Trophy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import CrewIdentityEditor from "@/components/CrewIdentityEditor";
import { api } from "@/lib/api";


export default function YourTinyCrewModal({ open, onOpenChange, board, leagueId, leagueName, me }) {
    const players = (board || []).filter(r => r.is_player !== false);
    const myRow = me?.id ? players.find(p => p.participant_id === me.id) : null;
    const [identityOpen, setIdentityOpen] = useState(false);

    // iter87.199.8 — Subscription-aware roster metric. Instead of the
    // legacy "0 pts" (which read Crown Race / Rivalry Points totals —
    // both now retired), show a per-crewmate rollup of Trophy Tokens
    // won across every completed tournament in the rivalry. Rules
    // (per operator):
    //   1. Pro+/Comp rivalry WITH ≥1 Trophy Token award → show `N tokens`
    //   2. Pro+/Comp rivalry WITHOUT any Trophy Token tournaments → hide the pts column
    //   3. Free-tier rivalry → hide the pts column (can't have Trophy Tokens anyway)
    // Endpoint returns an empty leaderboard for cases (2) and (3), so a
    // single "any tokens awarded?" check collapses to one branch.
    const [trophyTotals, setTrophyTotals] = useState({});
    useEffect(() => {
        if (!open || !leagueId) return;
        let cancelled = false;
        api.trophyTokensLeagueLeaderboard(leagueId).then(res => {
            if (cancelled) return;
            const totals = {};
            for (const row of (res?.leaderboard || [])) {
                if ((row.total_tokens || 0) > 0) totals[row.participant_id] = row.total_tokens;
            }
            setTrophyTotals(totals);
        }).catch(() => {
            if (cancelled) return;
            // Non-fatal — hides the pts column (matches "no tokens" state).
            setTrophyTotals({});
        });
        return () => { cancelled = true; };
    }, [open, leagueId]);

    const showTrophyColumn = Object.keys(trophyTotals).length > 0;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="tiny-crew-modal"
                className="max-w-md bg-ink border-2 border-cyber/40 max-h-[80dvh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display font-extrabold uppercase text-cyber">
                        <Users className="w-5 h-5" /> Your Tiny Crew
                    </DialogTitle>
                    <DialogDescription className="text-xs text-zinc-400 leading-relaxed">
                        Everyone in <b className="text-white">{leagueName || "this Tiny Crew Rivalry"}</b>
                        {showTrophyColumn
                            ? <> — ranked by <span className="text-volt font-bold">Trophy Tokens won</span> across every tournament.</>
                            : <> — your crewmates.</>}
                    </DialogDescription>
                </DialogHeader>

                {/* iter84.23 — Inline self-service identity editor. Only the
                    current user sees this. Collapsed by default to avoid
                    overshadowing the roster; the trigger is a tiny pill at
                    the top that expands into the full picker. */}
                {myRow && (
                    <div className="border border-cyber/30 bg-cyber/[0.04] p-2" data-testid="tiny-crew-identity-section">
                        <button
                            type="button"
                            onClick={() => setIdentityOpen((v) => !v)}
                            data-testid="tiny-crew-identity-toggle"
                            className="w-full flex items-center justify-between gap-2 text-left"
                        >
                            <span className="flex items-center gap-2 min-w-0">
                                <Sparkles className="w-3.5 h-3.5 text-cyber shrink-0" />
                                <span className="text-xs text-zinc-300 truncate">
                                    Your crew identity:
                                    <span className="text-base mx-1">{myRow.avatar_emoji || "🙂"}</span>
                                    {myRow.crew_flair
                                        ? <span className="text-zinc-400 italic">"{myRow.crew_flair}"</span>
                                        : <span className="text-zinc-500 italic">no flair yet — add one!</span>}
                                </span>
                            </span>
                            <span className="text-cyber text-[10px] font-bold uppercase tracking-wider shrink-0 inline-flex items-center gap-1">
                                {identityOpen ? <>Hide <ChevronUp className="w-3 h-3" /></> : <>Edit <ChevronDown className="w-3 h-3" /></>}
                            </span>
                        </button>
                        {identityOpen && (
                            <div className="mt-3 pt-3 border-t border-cyber/20">
                                <CrewIdentityEditor
                                    participantId={me.id}
                                    initialAvatar={myRow.avatar_emoji}
                                    initialFlair={myRow.crew_flair}
                                />
                                <p className="text-[10px] text-zinc-500 italic mt-2">
                                    Refresh the dashboard to see your new identity propagate to the leaderboard.
                                </p>
                            </div>
                        )}
                    </div>
                )}
                {players.length === 0 ? (
                    <p className="text-sm text-zinc-500 italic py-6 text-center">No crewmates yet — invite someone with the code above.</p>
                ) : (
                    <ul className="space-y-1.5" data-testid="tiny-crew-list">
                        {/* iter87.199.8 — When Trophy Tokens are active in
                            the rivalry, rank crewmates by their season-
                            total tokens (descending). Otherwise fall back
                            to the board-supplied rank so admins/co-admins
                            still cluster naturally. */}
                        {(() => {
                            const sorted = showTrophyColumn
                                ? [...players].sort((a, b) => (trophyTotals[b.participant_id] || 0) - (trophyTotals[a.participant_id] || 0))
                                : players;
                            // iter87.527 (user-requested) — Tied token totals
                            // now share the same rank (e.g. 2,2,2,2 instead of
                            // 2,3,4,5); the next distinct total still skips
                            // ahead to its true position (e.g. 6, not 3).
                            let lastTokens = null;
                            let lastRank = 0;
                            return sorted.map((p, i) => {
                                const pid = p.participant_id;
                                const isMe = me?.id === pid;
                                const tokens = trophyTotals[pid] || 0;
                                let rank;
                                if (showTrophyColumn) {
                                    rank = (lastTokens !== null && tokens === lastTokens) ? lastRank : i + 1;
                                    lastTokens = tokens;
                                    lastRank = rank;
                                } else {
                                    rank = p.rank || i + 1;
                                }
                                return (
                                <li
                                    key={pid}
                                    data-testid={`tiny-crew-row-${pid}`}
                                    className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm ${isMe ? "bg-cyber/10 border border-cyber/40" : "hover:bg-zinc-900/50"}`}
                                >
                                    <span className="font-mono text-xs text-zinc-500 w-7 shrink-0" data-testid={`tiny-crew-rank-${pid}`}>#{rank}</span>
                                    <span className="text-base shrink-0 w-6 text-center" data-testid={`tiny-crew-avatar-${pid}`} title={p.avatar_emoji ? "" : "No avatar yet"}>
                                        {p.avatar_emoji || <span className="text-zinc-600 text-xs">🙂</span>}
                                    </span>
                                    <span className={`flex-1 min-w-0 ${isMe ? "text-cyber" : "text-white"}`}>
                                        <span className="font-bold truncate inline-block max-w-full">
                                            {p.username}
                                            {isMe && <span className="ml-1.5 text-[10px] uppercase tracking-widest text-cyber">(you)</span>}
                                        </span>
                                        {p.crew_flair && (
                                            <span className="block text-[10px] text-zinc-500 italic truncate" data-testid={`tiny-crew-flair-${pid}`}>
                                                {p.crew_flair}
                                            </span>
                                        )}
                                    </span>
                                    {p.is_admin && (
                                        <span className="pill bg-cyber/20 border-cyber text-cyber text-[9px] py-0 px-1.5" title="Rivalry admin">
                                            <Shield className="w-2.5 h-2.5" /> ADMIN
                                        </span>
                                    )}
                                    {p.is_co_admin && !p.is_admin && (
                                        <span className="pill bg-cyber/10 border-cyber/40 text-cyber text-[9px] py-0 px-1.5" title="Co-admin">
                                            <Crown className="w-2.5 h-2.5" /> CO
                                        </span>
                                    )}
                                    {showTrophyColumn && (
                                        <span
                                            data-testid={`tiny-crew-tokens-${pid}`}
                                            title={`${tokens} Trophy Tokens won across the rivalry`}
                                            className="inline-flex items-center gap-1 font-mono text-xs text-volt w-16 justify-end shrink-0"
                                        >
                                            <Trophy className="w-3 h-3" />
                                            {tokens}
                                        </span>
                                    )}
                                </li>
                                );
                            });
                        })()}
                    </ul>
                )}
                <div className="flex justify-end pt-3 mt-3 border-t border-zinc-800">
                    <button
                        type="button"
                        data-testid="tiny-crew-close-btn"
                        onClick={() => onOpenChange?.(false)}
                        className="brutal-btn-ghost text-xs py-2 px-3"
                    >
                        <X className="w-3 h-3" /> Close
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
